import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X,
  Download,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EarningsBreakdown = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('this_month');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchEarningsData();
    }
  }, [isOpen]);

  const fetchEarningsData = async () => {
    try {
      setLoading(true);
      
      const [jobsResponse, commissionsResponse] = await Promise.all([
        axios.get(`${API}/jobs`),
        user.is_admin ? axios.get(`${API}/admin/commissions`) : Promise.resolve({ data: [] })
      ]);

      // Filter jobs where current user was the receiver and job is completed
      const myCompletedJobs = jobsResponse.data.filter(job => 
        job.accepted_by === user.id && job.status === 'completed'
      );
      
      setJobs(myCompletedJobs);
      
      // Filter commissions for this user
      const myCommissions = commissionsResponse.data.filter(commission => 
        commission.receiver_id === user.id
      );
      
      setCommissions(myCommissions);
    } catch (error) {
      console.error('Failed to fetch earnings data:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const calculateCommission = (price) => {
    if (price < 1000) {
      return { originator: 50, platform: 50, receiver: price - 100 };
    } else if (price <= 5000) {
      const fee = price * 0.1;
      return { originator: fee / 2, platform: fee / 2, receiver: price - fee };
    } else {
      const originator = price * 0.025;
      const platform = Math.min(price * 0.025, 500);
      return { originator, platform, receiver: price - originator - platform };
    }
  };

  const getCommissionBand = (price) => {
    if (price < 1000) return 'Fixed $50 each';
    if (price <= 5000) return '10% split (5% each)';
    return '5% split (2.5% each)';
  };

  const getFilteredJobs = () => {
    const now = new Date();
    let startDate, endDate;

    switch (activeTab) {
      case 'this_week':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 6);
        break;
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'custom':
        if (!customDateFrom || !customDateTo) return jobs;
        startDate = new Date(customDateFrom);
        endDate = new Date(customDateTo + 'T23:59:59');
        break;
      default:
        return jobs;
    }

    return jobs.filter(job => {
      const completedDate = new Date(job.completed_at);
      return completedDate >= startDate && completedDate <= endDate;
    });
  };

  const filteredJobs = getFilteredJobs();
  
  const totalEarnings = filteredJobs.reduce((sum, job) => {
    const commission = calculateCommission(job.quoted_price);
    return sum + commission.receiver;
  }, 0);

  const formatJobCode = (id) => {
    return `JOP-${id.slice(0, 8).toUpperCase()}`;
  };

  const exportCSV = () => {
    const headers = ['Job Code', 'Quoted Price', 'Commission Band', 'Originator Fee', 'Platform Fee', 'Your Earnings', 'Completed At'];
    const csvData = filteredJobs.map(job => {
      const commission = calculateCommission(job.quoted_price);
      return [
        formatJobCode(job.id),
        `$${job.quoted_price}`,
        getCommissionBand(job.quoted_price),
        `$${commission.originator.toFixed(2)}`,
        `$${commission.platform.toFixed(2)}`,
        `$${commission.receiver.toFixed(2)}`,
        new Date(job.completed_at).toLocaleDateString()
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jop-earnings-${user.business_name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Earnings Breakdown</h2>
            <p className="text-sm text-gray-600 mt-1">
              Estimated earnings from completed jobs (manual payouts during pilot)
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={exportCSV}
              disabled={filteredJobs.length === 0}
              className="jop-button-secondary flex items-center disabled:opacity-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="mb-6">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    {[
                      { id: 'this_week', label: 'This Week' },
                      { id: 'this_month', label: 'This Month' },
                      { id: 'custom', label: 'Custom Range' }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeTab === tab.id
                            ? 'border-amber-500 text-amber-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Custom Date Range */}
                {activeTab === 'custom' && (
                  <div className="mt-4 flex items-center space-x-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                      <input
                        type="date"
                        value={customDateFrom}
                        onChange={(e) => setCustomDateFrom(e.target.value)}
                        className="jop-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                      <input
                        type="date"
                        value={customDateTo}
                        onChange={(e) => setCustomDateTo(e.target.value)}
                        className="jop-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Summary Card */}
              <div className="jop-card p-6 mb-6">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <TrendingUp className="h-8 w-8 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Total Estimated Earnings</h3>
                  </div>
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    ${totalEarnings.toFixed(2)}
                  </div>
                  <p className="text-sm text-gray-600">
                    From {filteredJobs.length} completed job{filteredJobs.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Jobs Table */}
              {filteredJobs.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No completed jobs found</h3>
                  <p className="text-gray-600">
                    {activeTab === 'custom' 
                      ? 'Try adjusting your date range or complete some jobs to see earnings data.'
                      : 'Complete some jobs to see your earnings breakdown.'}
                  </p>
                </div>
              ) : (
                <div className="jop-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Job Code
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quoted Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Commission Band
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Originator Fee
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Platform Fee
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Your Earnings
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Completed At
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredJobs.map((job) => {
                          const commission = calculateCommission(job.quoted_price);
                          return (
                            <tr key={job.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {formatJobCode(job.id)}
                                </div>
                                <div className="text-xs text-gray-500">{job.title}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  ${job.quoted_price}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {getCommissionBand(job.quoted_price)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  ${commission.originator.toFixed(2)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  ${commission.platform.toFixed(2)}
                                  {job.quoted_price > 5000 && commission.platform >= 500 && (
                                    <span className="text-xs text-amber-600 block">Capped</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-green-600">
                                  ${commission.receiver.toFixed(2)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {new Date(job.completed_at).toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {new Date(job.completed_at).toLocaleTimeString()}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-900">
                        Total ({filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''})
                      </span>
                      <span className="text-lg font-bold text-green-600">
                        ${totalEarnings.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EarningsBreakdown;